import React, { useEffect, useState } from 'react';

import {
  faFileText,
  faTag,
  faUser,
  faLightbulb,
  faCalendar,
  faEdit,
  faTrash,
  faCheckCircle,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Card,
  Badge,
  Pagination,
  Group,
  Text,
  LoadingOverlay,
  Alert,
  Stack,
  ActionIcon,
  Tooltip,
  Modal,
  Button
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';

import { type Survey } from './types';
import { PostJSON, usePostJSON } from '../../utils/request';

interface SurveyResponse {
  success: boolean;
  surveys: Survey[];
  total: number;
  page: number;
  limit: number;
}

const PLACEHOLDERS = [
  '{{CLIENTNAME}}'
];

const renderPromptWithBadges = (prompt: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let keyCounter = 0;

  // Find all placeholder matches with their positions
  const matches: Array<{ placeholder: string; index: number }> = [];
  for (const placeholder of PLACEHOLDERS) {
    let searchIndex = 0;
    while (true) {
      const index = prompt.indexOf(placeholder, searchIndex);
      if (index === -1) break;
      matches.push({ placeholder, index });
      searchIndex = index + 1;
    }
  }

  // Sort matches by index
  matches.sort((a, b) => a.index - b.index);

  // Build parts array
  for (const match of matches) {
    // Add text before placeholder
    if (match.index > lastIndex) {
      const textPart = prompt.substring(lastIndex, match.index);
      if (textPart) {
        parts.push(
          <Text key={`text-${keyCounter++}`} component="span" fw={500} size="md">
            {textPart}
          </Text>
        );
      }
    }

    // Add badge for placeholder
    parts.push(
      <Badge
        key={`badge-${keyCounter++}`}
        color="blue"
        component="span"
        size="sm"
        variant="light"
      >
        {match.placeholder}
      </Badge>
    );

    lastIndex = match.index + match.placeholder.length;
  }

  // Add remaining text after last placeholder
  if (lastIndex < prompt.length) {
    const textPart = prompt.substring(lastIndex);
    if (textPart) {
      parts.push(
        <Text key={`text-${keyCounter++}`} component="span" fw={500} size="md">
          {textPart}
        </Text>
      );
    }
  }

  // If no placeholders found, return the original text
  if (parts.length === 0) {
    return [
      <Text key="text-0" component="span" fw={500} size="md">
        {prompt}
      </Text>
    ];
  }

  return parts;
};

const SurveyList: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [deletingSurvey, setDeletingSurvey] = useState<Survey | null>(null);

  const {
    data,
    error,
    loading: isLoading,
    refetch
  } = usePostJSON<SurveyResponse>(
    '/api/survey/data',
    { page, limit },
    [page, limit]
  );

  useEffect(() => {
    // ensure refetch is stable when dependencies change
    void refetch();
  }, [refetch]);

  const totalPages = Math.ceil((data?.total || 0) / limit);
  const surveys = data?.surveys || [];

  const handleDeleteSurvey = (survey: Survey) => {
    setDeletingSurvey(survey);
  };

  const handleConfirmDelete = async () => {
    if (!deletingSurvey) return;

    try {
      await PostJSON('/api/survey/delete', {
        id: deletingSurvey.id
      });
      
      setDeletingSurvey(null);
      void refetch();
      
      notifications.show({
        title: 'Success',
        message: `Survey "${deletingSurvey.name}" has been deleted successfully.`,
        color: 'green'
      });
    } catch (error) {
      console.error('Error deleting survey:', error);
      
      notifications.show({
        title: 'Error',
        message: 'Failed to delete survey. Please try again.',
        color: 'red'
      });
    }
  };

  if (error) {
    return (
      <Alert color="red" title="Error">
        {error.message}
      </Alert>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={isLoading} />

      {surveys.length === 0 ? (
        <Text c="dimmed" py="xl" ta="center">
          No surveys found.
        </Text>
      ) : (
        <>
          <Stack gap="md">
            {surveys.map((survey) => (
              <Card key={survey.id} withBorder p="md" shadow="sm">
                <Stack gap="xs">
                  <Group gap="xs" justify="space-between">
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faFileText} size="sm" />
                      <Text c="dimmed" size="sm">{survey.name}</Text>
                      <Badge
                        color={survey.isEnabled !== false ? 'green' : 'red'}
                        leftSection={
                          <FontAwesomeIcon
                            icon={survey.isEnabled !== false ? faCheckCircle : faTimesCircle}
                            size="xs"
                          />
                        }
                        size="sm"
                        variant="light"
                      >
                        {survey.isEnabled !== false ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </Group>
                    <Group gap="xs">
                      <Tooltip label="Edit Survey">
                        <ActionIcon
                          color="blue"
                          component={Link}
                          to={`/survey/${survey.id}/edit`}
                          variant="subtle"
                        >
                          <FontAwesomeIcon icon={faEdit} size="sm" />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete Survey">
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => handleDeleteSurvey(survey)}
                        >
                          <FontAwesomeIcon icon={faTrash} size="sm" />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                  {survey.description && (
                    <Text c="dimmed" mt={-4} size="sm">
                      {survey.description}
                    </Text>
                  )}
                  {survey.content?.questions && survey.content.questions.length > 0 && (
                    <Stack gap={4} pl="md">
                      {survey.content.questions.map((question, idx) => (
                        <Card key={idx} withBorder p="xs" shadow="xs">
                          <Text component="div" fw={500} size="md">
                            {renderPromptWithBadges(question.prompt)}
                          </Text>
                        </Card>
                      ))}
                    </Stack>
                  )}
                  <Group gap="md" mt="xs" wrap="wrap">
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faUser} size="sm" />
                      <Text size="sm">{survey.who}</Text>
                    </Group>
                    {survey.intent && (
                      <Group gap="xs">
                        <FontAwesomeIcon icon={faLightbulb} size="sm" />
                        <Text size="sm">{survey.intent}</Text>
                      </Group>
                    )}
                    {survey.tags && survey.tags.length > 0 && (
                      <Group gap="xs" wrap="wrap">
                        {survey.tags.map((tag, idx) => (
                          <Badge
                            key={idx}
                            color="gray"
                            leftSection={<FontAwesomeIcon icon={faTag} size="xs" />}
                            size="sm"
                            variant="light"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </Group>
                    )}
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faCalendar} size="sm" />
                      <Text c="dimmed" size="sm">
                        {new Date(survey.updatedAt).toLocaleDateString()}
                      </Text>
                    </Group>
                  </Group>
                </Stack>
              </Card>
            ))}
          </Stack>

          {totalPages > 1 && (
            <Group justify="center" mt="md">
              <Pagination
                total={totalPages}
                value={page}
                onChange={setPage}
              />
            </Group>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        opened={!!deletingSurvey}
        size="sm"
        title="Delete Survey"
        onClose={() => setDeletingSurvey(null)}
      >
        {deletingSurvey && (
          <Stack gap="md">
            <Text>
              Are you sure you want to delete the survey&nbsp;
              <Text component="span" fw={700}>
                {deletingSurvey.name}
              </Text>
              ? This action cannot be undone.
            </Text>
            <Group gap="sm" justify="flex-end">
              <Button variant="outline" onClick={() => setDeletingSurvey(null)}>
                Cancel
              </Button>
              <Button color="red" onClick={() => void handleConfirmDelete()}>
                Delete
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
};

export default SurveyList;

